import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IEntrypointResponse, IHttpOptions } from '../interfaces/httpOptions.interface';
import { ILink } from '../interfaces/link.interface';
import { IHateoas } from './hateoas.interface';

/**
 * @internal
 */
@Injectable({
  providedIn: 'root',
})
export class HateoasService {
  entryPoint: string;
  httpOptions: IHttpOptions;
  links: ILink[];
  useHrefs: boolean = false;

  /**
   * Método que retorna uma url baseado na relation ou href informada.
   *
   * Method that returns a url based on the inputed relation or href.
   *
   * @param relation Recebe o atributo 'rel' ou 'href' do retorno da API no padrão HATEOAS
   */
  getURL(relation: string, charonID: string, response: IEntrypointResponse): string {
    return this.isEntryPoint(relation)
      ? relation
      : this.getLinkOnState(relation, charonID, response);
  }

  /**
   * Método que returna um novo link armazenado em memória basedo na relation.
   *
   * Method that returns a new link based on the relation.
   * @internal
   * @param relation  Recebe o atributo 'rel' do retorno da API no padrão HATEOAS
   * @returns a link `href` based on relation param.
   */
  private getLinkOnState(relation: string, charonID: string, response: IEntrypointResponse): string {
    const link = this.getLink(relation, charonID, response);

    if (!link) {
      throw new Error(`Nenhum Link com a relation '${relation}' foi encontrado`);
    }

    return link;
  }

  /**
   * Método que verifica se url inputada é um entry point.
   *
   * Method that verifies if inputed url is an entry point.
   *
   * @internal
   * @param url string
   */
  private isEntryPoint(url: string) {
    return url === this.entryPoint;
  }

  /**
   * Método que retorna todas as relations e links existentes em memória.
   *
   * Method that returns all relations and links in memory.
   *
   * @returns all `Links` for hateoas relations.
   */
  getRelations(): ILink[] {
    return this.links;
  }

  /**
   * Método que retorna uma lista de links para determinada relation ou href.
   *
   * Method that returns a links list for inputed relation.
   *
   * @internal
   * @param relation  Recebe o atributo 'rel' ou 'href' do retorno da API no padrão HATEOAS
   * @returns a `Link` if relations existis.
   */
  getLink(relation: string, charonID: string, response?: IEntrypointResponse): string | undefined {
    const hasResponseLinks = response?.links && response.links.length > 0;
    const hasLinks = this.links?.length > 0;

    if (this.useHrefs) {
      return relation;
    }
    if (hasResponseLinks) {
      const responseLinks = response.links.find(element => element.rel === relation);
      return responseLinks?.href;
    }

    if (hasLinks) {
      const [link] = this.links.filter((link) => link.rel === relation);
      return link.href;
    }

    return undefined;
  }

  /**
   * Método para adicionar links em memória.
   *
   * Method that pushes a new link in memory.
   *
   * @param links Array de links retornados pela API
   */
  setLinks(links: ILink[]) {
    const isNoLinks = !links || links.length === 0;

    if (isNoLinks) {
      return;
    }
    this.links = [...links];
  }

  /**
   * Método para setar configurações padrão(IConfig) em memória dentro do HateoasService.
   *
   * Method that sets default configuration(IConfig) in memory on HateoasService.
   *
   * @internal
   * @param config Recebe as configurações de entrypoint e httpOptions da interface IHateoas
   */
  setConfig({ entryPoint: entrypoint, httpOptions }: IHateoas) {
    this.entryPoint = entrypoint || this.entryPoint;
    this.httpOptions = {
      ...this.httpOptions,
      ...httpOptions,
      headers: this.merge<IHttpOptions['headers']>(HttpHeaders, this.httpOptions?.headers, httpOptions?.headers),
      params: this.merge<IHttpOptions['params']>(HttpParams, this.httpOptions?.params, httpOptions?.params),
    };
  }

  /**
   * Método para setar busca por Href.
   *
   * Method that sets search by Href.
   *
   * @internal
   * @param useHrefs Recebe um boolean para definir se a busca será por Href ou por Relations
   */
  setUseHrefs(useHrefs: boolean) {
    this.useHrefs = useHrefs;
  }

  /**
   * Método que faz o merge dos HttpOptions header ou params.
   *
   * Method that merges HttpHeaders header or params.
   *
   * @internal
   * @param c Recebe um objeto do tipo HttpHeaders ou HttpParam
   * @param current Recebe o valor do header ou param definido globalmente
   * @param additional Recebe o valor do header ou param passado via parâmetro na chamada do método
   */
  private merge<T extends IHttpOptions['headers'] | IHttpOptions['params']>(
    c: new (t?: any) => T,
    current?: T,
    additional?: T,
  ): T {
    const newCurrent = current instanceof c
      ? current
      : new c(current);

    if (!additional) {
      return newCurrent;
    }

    const newAdditional = additional instanceof c
      ? additional
      : new c(additional);

    return (newAdditional as HttpHeaders | HttpParams)
      .keys()
      .reduce(
        // tslint:disable-next-line: no-non-null-assertion
        (headers: any, key: any) => headers.set(key, (newAdditional as HttpHeaders | HttpParams).get(key)!),
        newCurrent,
      );
  }

  findLink(links: ILink[], relation: string) {
    if (this.useHrefs) {
      return relation;
    }
    const [link] = links.filter(element => element.rel === relation);
    return link.href;
  }

}
